import React, { useEffect, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Loader2, MessagesSquare, Megaphone, Film, ChefHat, Play } from "lucide-react";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { api, formatApiError, fileUrl } from "../lib/api";
import { FeedVideo } from "../components/FeedVideo";
import { AppHeader } from "../components/AppHeader";
import { BottomSheet } from "../components/BottomSheet";
import { MediaUploader } from "../components/MediaUploader";
import { Fab, Empty, Avatar, Thread } from "./Hangouts";
import { ReunionThread } from "./Reunions";

const ago = (d) => { try { return formatDistanceToNow(new Date(d), { addSuffix: true }); } catch { return ""; } };

const filters = [
  { key: "all", label: "All" },
  { key: "hangout", label: "Hangouts" },
  { key: "reunion", label: "Reunions" },
  { key: "story", label: "Stories" },
  { key: "recipe", label: "Recipes" },
];

const typeMeta = {
  hangout: { icon: MessagesSquare, label: "Hangout", color: "#8AB4F8" },
  reunion: { icon: Megaphone, label: "Reunion", color: "#7C83FF" },
  story: { icon: Film, label: "Story", color: "#C58AF0" },
  recipe: { icon: ChefHat, label: "Recipe", color: "#7FD69A" },
};

export default function Memories() {
  const [filter, setFilter] = useState("all");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(false);
  const [activeThread, setActiveThread] = useState(null);
  const [activeReunion, setActiveReunion] = useState(null);

  const load = useCallback(async (f) => {
    setLoading(true);
    try { const { data } = await api.get(`/memories?type=${f}`); setItems(data); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(filter); }, [filter, load]);

  if (activeThread) return <Thread postId={activeThread} onBack={() => { setActiveThread(null); load(filter); }} />;
  if (activeReunion) return <ReunionThread reunionId={activeReunion} onBack={() => { setActiveReunion(null); load(filter); }} />;

  return (
    <div>
      <AppHeader title="Memories" />
      <div className="px-5 pt-3 pb-1 lg:px-8 lg:pt-6 lg:max-w-4xl lg:mx-auto">
        <div className="grid grid-cols-5 gap-1.5 lg:gap-2.5 lg:max-w-xl">
          {filters.map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`filter-${f.key}`}
              className="rounded-full py-2 text-[11px] font-medium transition-colors text-center leading-none"
              style={filter === f.key
                ? { background: "var(--accent)", color: "#fff" }
                : { background: "var(--overlay)", color: "var(--text-secondary)", border: "1px solid var(--border)" }}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="px-5 py-4 lg:px-8 lg:py-6 lg:max-w-4xl lg:mx-auto space-y-4 lg:space-y-5">
        {loading ? (
          <div className="grid place-items-center py-20"><Loader2 className="animate-spin gold-text" /></div>
        ) : items.length === 0 ? (
          <Empty text="No memories here yet. Tap + to add one." />
        ) : items.map((m) => <MemoryCard key={m.id} m={m} onOpen={
          m.type === "hangout" && m.linked_id ? () => setActiveThread(m.linked_id)
          : m.type === "reunion" && m.linked_id ? () => setActiveReunion(m.linked_id)
          : null
        } />)}
      </div>

      <Fab onClick={() => setComposer(true)} testid="new-memory-fab" />
      <Composer open={composer} onClose={() => setComposer(false)} onDone={() => { setComposer(false); load(filter); }} />
    </div>
  );
}

function MemoryCard({ m, onOpen }) {
  const meta = typeMeta[m.type] || typeMeta.story;
  return (
    <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
      onClick={onOpen || undefined} whileTap={onOpen ? { scale: 0.99 } : undefined}
      data-testid={`memory-${m.id}`}
      className={`card-surface rounded-2xl overflow-hidden ${onOpen ? "cursor-pointer" : ""}`}>
      <div className="p-4 lg:p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2.5">
            <Avatar name={m.author?.name} sm />
            <div>
              <div className="text-xs font-semibold">{m.author?.name}</div>
              <div className="text-[10px]" style={{ color: "var(--text-muted)" }}>{ago(m.created_at)}</div>
            </div>
          </div>
          <span className="inline-flex items-center gap-1 rounded-full px-2 py-1 text-[10px] uppercase tracking-wide"
            style={{ background: "var(--overlay)", color: meta.color }}>
            <meta.icon size={11} /> {meta.label}
          </span>
        </div>

        {m.type === "recipe" ? (
          <div className="mt-3">
            <h3 className="font-serif-display text-2xl leading-tight">{m.recipe_title || "Family Recipe"}</h3>
            {m.media && <img src={fileUrl(m.media)} alt="" className="mt-2 rounded-xl w-full max-h-60 object-cover" />}
            {m.ingredients && (<><Label t="Ingredients" /><p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{m.ingredients}</p></>)}
            {m.steps && (<><Label t="Steps" /><p className="text-sm whitespace-pre-wrap" style={{ color: "var(--text-secondary)" }}>{m.steps}</p></>)}
            {m.notes && (<><Label t="Notes" /><p className="text-sm whitespace-pre-wrap italic" style={{ color: "var(--text-muted)" }}>{m.notes}</p></>)}
          </div>
        ) : (
          <>
            {m.caption && <p className="text-sm mt-3 whitespace-pre-wrap">{m.caption}</p>}
            {m.media && m.media_kind === "video" && (
              <FeedVideo path={m.media} className="max-h-72" testid="memory-video" />
            )}
            {m.media && m.media_kind === "audio" && (
              <audio src={fileUrl(m.media)} controls className="mt-3 w-full" data-testid="memory-audio" />
            )}
            {m.media && m.media_kind === "image" && (
              <img src={fileUrl(m.media)} alt="" className="mt-3 rounded-xl w-full max-h-72 object-cover" />
            )}
          </>
        )}
        {onOpen && (
          <div className="flex items-center gap-1.5 mt-3 pt-3 text-xs" style={{ borderTop: "1px solid var(--hairline)", color: "var(--accent)" }}>
            <MessagesSquare size={13} /> View conversation
          </div>
        )}
      </div>
    </motion.div>
  );
}

const Label = ({ t }) => <div className="text-[11px] uppercase tracking-widest mt-3 mb-1 gold-text">{t}</div>;

function Composer({ open, onClose, onDone }) {
  const [step, setStep] = useState("type"); // type | form
  const [type, setType] = useState(null);
  const [linkOptions, setLinkOptions] = useState([]);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  const reset = () => { setStep("type"); setType(null); setForm({}); setLinkOptions([]); };
  const close = () => { reset(); onClose(); };

  const pick = async (t) => {
    setType(t);
    setForm({});
    if (t === "hangout" || t === "reunion") {
      try {
        const { data } = await api.get(t === "hangout" ? "/hangouts" : "/reunions");
        setLinkOptions(data);
      } catch { setLinkOptions([]); }
    }
    setStep("form");
  };

  const submit = async () => {
    setSaving(true);
    try {
      const payload = { type, ...form };
      await api.post("/memories", payload);
      toast.success("Memory saved");
      reset();
      onDone();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const inputStyle = { background: "var(--bg-surface)", border: "1px solid var(--border)", color: "var(--text-primary)" };
  const cls = "w-full rounded-xl px-4 py-3 text-sm outline-none focus:ring-2 focus:ring-[var(--accent)]";

  return (
    <BottomSheet open={open} onClose={close} title={step === "type" ? "New Memory" : typeMeta[type]?.label}>
      <AnimatePresence mode="wait">
        {step === "type" ? (
          <motion.div key="type" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <p className="text-sm mb-4" style={{ color: "var(--text-secondary)" }}>What are you posting?</p>
            <div className="grid grid-cols-2 gap-3">
              {["hangout", "reunion", "story", "recipe"].map((t) => {
                const M = typeMeta[t];
                return (
                  <motion.button whileTap={{ scale: 0.96 }} key={t} onClick={() => pick(t)}
                    data-testid={`memory-type-${t}`}
                    className="card-surface rounded-2xl p-5 flex flex-col items-center gap-2">
                    <M.icon size={26} style={{ color: M.color }} />
                    <span className="text-sm font-medium">{M.label}</span>
                  </motion.button>
                );
              })}
            </div>
          </motion.div>
        ) : (
          <motion.div key="form" initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0 }} className="space-y-3">
            {(type === "hangout" || type === "reunion") && (
              <>
                <label className="text-[11px] uppercase tracking-wide" style={{ color: "var(--text-muted)" }}>
                  Link to a {type}
                </label>
                <select value={form.linked_id || ""} onChange={(e) => {
                  const opt = linkOptions.find((o) => o.id === e.target.value);
                  setForm({ ...form, linked_id: e.target.value, caption: opt ? (opt.title || opt.content?.slice(0, 80)) : form.caption });
                }} data-testid="memory-link-select" className={cls} style={inputStyle}>
                  <option value="">Select…</option>
                  {linkOptions.map((o) => (
                    <option key={o.id} value={o.id}>{o.title || (o.content || "").slice(0, 40)}</option>
                  ))}
                </select>
                <textarea value={form.caption || ""} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={3}
                  placeholder="Add a caption for this memory…" data-testid="memory-caption-input" className={cls} style={inputStyle} />
              </>
            )}

            {type === "story" && (
              <>
                <textarea value={form.caption || ""} onChange={(e) => setForm({ ...form, caption: e.target.value })} rows={2}
                  placeholder="Tell the story…" data-testid="memory-caption-input" className={cls} style={inputStyle} />
                {form.media && (
                  <div className="rounded-xl overflow-hidden">
                    {form.media_kind === "video" && <video src={fileUrl(form.media)} controls className="w-full max-h-56" />}
                    {form.media_kind === "audio" && <audio src={fileUrl(form.media)} controls className="w-full" />}
                    {form.media_kind === "image" && <img src={fileUrl(form.media)} alt="" className="w-full max-h-56 object-cover" />}
                  </div>
                )}
                <div className="grid grid-cols-3 gap-2">
                  <MediaUploader label="Photo" kind="image" accept="image/*" testid="story-image-upload"
                    onUploaded={(p) => setForm((f) => ({ ...f, media: p, media_kind: "image" }))} />
                  <MediaUploader label="Video" kind="video" accept="video/*" testid="story-video-upload"
                    onUploaded={(p) => setForm((f) => ({ ...f, media: p, media_kind: "video" }))} />
                  <MediaUploader label="Voice" kind="audio" accept="audio/*" testid="story-audio-upload"
                    onUploaded={(p) => setForm((f) => ({ ...f, media: p, media_kind: "audio" }))} />
                </div>
              </>
            )}

            {type === "recipe" && (
              <>
                <input value={form.recipe_title || ""} onChange={(e) => setForm({ ...form, recipe_title: e.target.value })}
                  placeholder="Recipe name (e.g. Grandma's Jollof)" data-testid="recipe-title-input" className={cls} style={inputStyle} />
                {form.media && <img src={fileUrl(form.media)} alt="" className="rounded-xl w-full max-h-48 object-cover" />}
                <MediaUploader label="Dish photo" testid="recipe-image-upload"
                  onUploaded={(p) => setForm((f) => ({ ...f, media: p, media_kind: "image" }))} />
                <textarea value={form.ingredients || ""} onChange={(e) => setForm({ ...form, ingredients: e.target.value })} rows={3}
                  placeholder="Ingredients…" data-testid="recipe-ingredients-input" className={cls} style={inputStyle} />
                <textarea value={form.steps || ""} onChange={(e) => setForm({ ...form, steps: e.target.value })} rows={4}
                  placeholder="Steps…" data-testid="recipe-steps-input" className={cls} style={inputStyle} />
                <textarea value={form.notes || ""} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2}
                  placeholder="Family notes (optional)…" data-testid="recipe-notes-input" className={cls} style={inputStyle} />
              </>
            )}

            <motion.button whileTap={{ scale: 0.97 }} onClick={submit} disabled={saving}
              data-testid="memory-submit-btn"
              className="w-full rounded-xl py-3.5 font-semibold text-sm flex items-center justify-center gap-2"
              style={{ background: "var(--accent)", color: "var(--bg-base)" }}>
              {saving && <Loader2 size={16} className="animate-spin" />} Save Memory
            </motion.button>
            <button onClick={() => setStep("type")} className="w-full text-sm py-2" style={{ color: "var(--text-muted)" }}>
              ← Choose a different type
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </BottomSheet>
  );
}
